/**
 * The engine: a single pure reducer `reduce(state, action) -> EngineResult`.
 *
 * It validates the action against the rules, applies it immutably, emits a list
 * of events (handy for UI animation and a future network layer), and advances
 * the turn. It never throws on illegal input — it returns `{ ok: false }` with a
 * reason — so callers can surface bad moves without crashing.
 */
import {
  abilityDef,
  abilityDestinations,
  abilityRevives,
  abilitySources,
} from "./abilities";
import type { Action } from "./actions";
import { promotionRank, shift } from "./coords";
import {
  allLegalMoves,
  footprintPartner,
  legalDeploySquares,
  legalDeploySquaresForPiece,
  legalMovesFrom,
  mustDiscard,
} from "./moves";
import { pieceDef } from "./pieces";
import { isInCheck, otherPlayer } from "./rules";
import type {
  AbilityId,
  Card,
  GameState,
  LogEntry,
  Piece,
  PieceTypeId,
  PlayerId,
  PlayerState,
  Square,
  Vector,
} from "./types";

export type GameEvent =
  | { readonly type: "deployed"; readonly player: PlayerId; readonly piece: PieceTypeId; readonly to: Square }
  | { readonly type: "moved"; readonly from: Square; readonly to: Square }
  | { readonly type: "captured"; readonly piece: PieceTypeId; readonly at: Square }
  | { readonly type: "promoted"; readonly at: Square; readonly to: PieceTypeId }
  | { readonly type: "drew"; readonly player: PlayerId }
  | { readonly type: "passed"; readonly player: PlayerId }
  | { readonly type: "cardPlayed"; readonly player: PlayerId; readonly ability: AbilityId }
  | { readonly type: "revived"; readonly player: PlayerId; readonly piece: PieceTypeId }
  | { readonly type: "recalled"; readonly piece: PieceTypeId; readonly from: Square }
  | { readonly type: "discarded"; readonly player: PlayerId; readonly card: PieceTypeId | AbilityId }
  | { readonly type: "gameOver"; readonly winner: PlayerId | null };

export type EngineResult =
  | { readonly ok: true; readonly state: GameState; readonly events: readonly GameEvent[] }
  | { readonly ok: false; readonly error: string };

const fail = (error: string): EngineResult => ({ ok: false, error });

/**
 * A game is declared a draw after this many consecutive plies with no
 * irreversible progress (no capture, deploy, draw, or pawn advance). Because
 * each kind of progress is itself finite — captures, the hand, the deck, and a
 * pawn's march all run out — this guarantees every game ends.
 */
export const DRAW_AFTER_IDLE_PLIES = 60;

/**
 * Hard cap on total game length. Once this many plies have been played the game
 * is a draw regardless of progress — a backstop above the idle-draw rule so a
 * game with slow, steady progress still terminates. A "turn" in the UI is a
 * full move (both players act), so this is 2 plies per turn: 120 plies = 60
 * turns. Overridable per game via config.maxPlies.
 */
export const DEFAULT_MAX_PLIES = 120;

// --- small immutable helpers -------------------------------------------------

function replaceAt<T>(arr: readonly T[], idx: number, value: T): T[] {
  const next = arr.slice();
  next[idx] = value;
  return next;
}

function updatePlayer(
  players: readonly [PlayerState, PlayerState],
  id: PlayerId,
  patch: Partial<PlayerState>,
): [PlayerState, PlayerState] {
  const next = players.slice() as [PlayerState, PlayerState];
  next[id] = { ...next[id], ...patch };
  return next;
}

function pieceId(prefix: string, player: PlayerId, ply: number): string {
  return `${prefix}${player}-${ply}`;
}

/**
 * The board after a relocate ability (Dash/Sidestep) moves the piece on `from`
 * to `to`. Link-aware: two-cell terrain shifts as a unit. Occupancy only — it
 * does NOT apply promotion (the caller adjusts the destination type when a
 * pawn/farmer lands on its promotion rank). Shared by the reducer and the
 * checkmate test so the two can never disagree about what a card does.
 */
function relocateBoard(state: GameState, from: Square, to: Square): (Piece | null)[] {
  const piece = state.board[from]!;
  if (piece.link == null) {
    const cleared = replaceAt(state.board, from, null);
    return replaceAt(cleared, to, { ...piece, hasMoved: true });
  }
  const partner = state.board[piece.link]!;
  const w = state.config.width;
  const v: Vector = [(to % w) - (from % w), Math.floor(to / w) - Math.floor(from / w)];
  const np = shift(piece.link, v, state.config)!; // validity guaranteed by abilityDestinations
  let board = replaceAt(state.board, from, null);
  board = replaceAt(board, piece.link, null);
  board = replaceAt(board, to, { ...piece, hasMoved: true, link: np });
  board = replaceAt(board, np, { ...partner, hasMoved: true, link: to });
  return board;
}

/** The board after a Recall removes the piece on `from` (and its linked tile). */
function recallBoard(state: GameState, from: Square): (Piece | null)[] {
  const piece = state.board[from]!;
  let board = replaceAt(state.board, from, null);
  if (piece.link != null) board = replaceAt(board, piece.link, null);
  return board;
}

/** The reason an ability is rejected for not addressing — or for causing — check. */
function inCheckRejection(state: GameState, player: PlayerId): string {
  return isInCheck(state, player)
    ? "You're in check — that doesn't get you out of it."
    : "That would leave your King in check.";
}

/**
 * Advance ply + turn after a non-terminal action, then run the new side's
 * start-of-turn draw. Returns the next state plus any events it generated (the
 * automatic draw), which the caller folds into the action's event list.
 */
function endTurn(state: GameState): { state: GameState; events: GameEvent[] } {
  const flipped = { ...state, ply: state.ply + 1, turn: otherPlayer(state.turn) };
  return autoDraw(flipped);
}

/**
 * The start-of-turn draw. Drawing is no longer an action the player spends their
 * turn on — every turn begins with the side to move automatically taking the top
 * card of their deck, so their single action is always to move a piece or play a
 * card. Does nothing when the deck is empty or the hand is already at the limit
 * (so a maxed hand isn't churned through a forced discard every turn). The draw
 * is ambient: it does not touch the idle/progress counter and uses no RNG (the
 * deck was shuffled once at setup), so replays stay deterministic.
 *
 * The game's very first turn is played from the dealt opening hand — there is no
 * earlier turn to have ended — which also trims the first player's tempo edge.
 */
function autoDraw(state: GameState): { state: GameState; events: GameEvent[] } {
  const player = state.turn;
  const p = state.players[player];
  if (p.deck.length === 0 || p.hand.length >= state.config.handLimit) {
    return { state, events: [] };
  }
  const [top, ...rest] = p.deck;
  const players = updatePlayer(state.players, player, {
    deck: rest,
    hand: [...p.hand, top as Card],
  });
  return { state: { ...state, players }, events: [{ type: "drew", player }] };
}

// --- the reducer -------------------------------------------------------------

export function reduce(state: GameState, action: Action): EngineResult {
  if (state.phase !== "playing") return fail("Game is over.");
  // Resignation and draw negotiation may happen on either side's turn, so
  // handle them before the turn-ownership check.
  if (action.type === "RESIGN") return applyResign(state, action.player);
  if (action.type === "OFFER_DRAW") return applyOfferDraw(state, action.player);
  if (action.type === "ACCEPT_DRAW") return applyAcceptDraw(state, action.player);
  if (action.type === "DECLINE_DRAW") return applyDeclineDraw(state, action.player);
  if (action.player !== state.turn) return fail("Not your turn.");

  // A hand pushed over the limit by a card effect must be discarded down before
  // anything else (a clock TIMEOUT still applies — it ends the game regardless).
  if (mustDiscard(state, action.player) && action.type !== "DISCARD" && action.type !== "TIMEOUT") {
    return fail(`Discard down to ${state.config.handLimit} cards first.`);
  }

  switch (action.type) {
    case "MOVE":
      return applyMove(state, action.player, action.from, action.to);
    case "DISCARD":
      return applyDiscard(state, action.player, action.cardId);
    case "DEPLOY":
      return applyDeploy(state, action.player, action.cardId, action.to);
    case "PLAY_CARD":
      return applyPlayCard(
        state,
        action.player,
        action.cardId,
        action.from,
        action.to,
        action.reviveType,
      );
    case "DRAW":
      return applyDraw(state, action.player);
    case "PASS":
      return applyPass(state, action.player);
    case "TIMEOUT":
      return applyTimeout(state, action.player);
    case "CAPTAIN_MOVE":
      return applyCaptainMove(state, action.player, action.from, action.to);
  }
}

/**
 * Has the side to move already used their Captain's free move this turn?
 * Derived from the log: the Captain move doesn't advance the turn, so the
 * current turn's entries are the trailing run by the side to move.
 */
export function captainMovedThisTurn(state: GameState): boolean {
  for (let i = state.log.length - 1; i >= 0; i--) {
    const e = state.log[i]!;
    if (!("player" in e) || e.player !== state.turn) break;
    if (e.kind === "captainMove") return true;
  }
  return false;
}

/**
 * The Captain's free move: one per turn, and it does NOT end the turn (the
 * player still takes a normal action afterwards). It must be a legal move (so it
 * can't leave the player's own King in check).
 */
function applyCaptainMove(
  state: GameState,
  player: PlayerId,
  from: Square,
  to: Square,
): EngineResult {
  const piece = state.board[from];
  if (!piece || piece.owner !== player || piece.type !== "captain") {
    return fail("That isn't your Captain.");
  }
  if (captainMovedThisTurn(state)) return fail("Your Captain has already moved this turn.");
  if (!legalMovesFrom(state, from).some((m) => m.to === to)) return fail("Illegal Captain move.");

  const events: GameEvent[] = [];
  const victim = state.board[to];
  let players = state.players;
  if (victim) {
    events.push({ type: "captured", piece: victim.type, at: to });
    players = updatePlayer(players, player, {
      captured: [...state.players[player].captured, victim.type],
    });
  }
  let board = replaceAt(state.board, from, null);
  board = replaceAt(board, to, { ...piece, hasMoved: true });
  events.push({ type: "moved", from, to });

  const log: LogEntry = {
    kind: "captainMove",
    player,
    from,
    to,
    captured: victim ? victim.type : null,
    ply: state.ply,
  };
  // Keep the same turn — only board/players/log advance.
  return {
    ok: true,
    state: {
      ...state,
      board,
      players,
      log: [...state.log, log],
      idlePlies: victim ? 0 : state.idlePlies + 1,
      ply: state.ply + 1,
    },
    events,
  };
}

function applyMove(
  state: GameState,
  player: PlayerId,
  from: Square,
  to: Square,
): EngineResult {
  const piece = state.board[from];
  if (!piece || piece.owner !== player) return fail("No piece of yours there.");

  const legal = legalMovesFrom(state, from).find((m) => m.to === to);
  if (!legal) return fail("Illegal move.");

  const events: GameEvent[] = [];
  const victim = state.board[to];
  let players = state.players;

  if (victim) {
    events.push({ type: "captured", piece: victim.type, at: to });
    players = updatePlayer(players, player, {
      captured: [...state.players[player].captured, victim.type],
    });
  }

  // Resolve promotion on arrival.
  const def = pieceDef(piece.type);
  let movedType = piece.type;
  if (def.promotesTo && rankOf(to, state) === promotionRank(player, state.config)) {
    movedType = def.promotesTo;
    events.push({ type: "promoted", at: to, to: movedType });
  }

  const movedPiece: Piece = { ...piece, type: movedType, hasMoved: true };
  let board = replaceAt(state.board, from, null);
  board = replaceAt(board, to, movedPiece);
  events.push({ type: "moved", from, to });

  const log: LogEntry = {
    kind: "move",
    player,
    piece: piece.type,
    from,
    to,
    captured: victim ? victim.type : null,
    promotedTo: movedType !== piece.type ? movedType : null,
    ply: state.ply,
  };

  // Captures and pawn advances are irreversible — they reset the idle counter.
  const progress = victim !== null || piece.type === "pawn";
  return finish(state, { board, players, log }, events, progress);
}

function applyDeploy(
  state: GameState,
  player: PlayerId,
  cardId: string,
  to: Square,
): EngineResult {
  const hand = state.players[player].hand;
  const card = hand.find((c) => c.id === cardId);
  if (!card) return fail("Card not in hand.");
  if (card.kind !== "piece") return fail("That card cannot be deployed.");

  const def = pieceDef(card.piece);
  // The Prince isn't deck-buildable, but a Resurrected one can be redeployed.
  if (!def.deployable && card.piece !== "prince") return fail("That piece cannot be deployed.");
  if (!legalDeploySquaresForPiece(state, player, card.piece).includes(to)) {
    return fail(
      def.placeAnywhere
        ? "You can only deploy onto an empty square."
        : "You can only deploy onto an empty home square.",
    );
  }

  const newPiece: Piece = {
    id: pieceId("d", player, state.ply),
    type: card.piece,
    owner: player,
    hasMoved: false,
  };
  let board: (Piece | null)[];
  // Two-cell terrain (Valley/Ridge) drops a second tile on its partner cell
  // (already verified empty + on-board) and links the pair so they act as one.
  if (def.footprint) {
    const partner = footprintPartner(to, def.footprint, state.config)!;
    board = replaceAt(state.board, to, { ...newPiece, link: partner });
    board = replaceAt(board, partner, {
      id: pieceId("d2", player, state.ply),
      type: card.piece,
      owner: player,
      hasMoved: false,
      link: to,
    });
  } else {
    board = replaceAt(state.board, to, newPiece);
  }

  // A deploy may not leave your own King in check. When you're in check this is
  // exactly the "block the check" rule: the only legal deploys are ones that
  // interpose a piece and resolve it.
  if (isInCheck({ ...state, board }, player)) {
    return fail(
      isInCheck(state, player)
        ? "You're in check — that deployment doesn't block it."
        : "That deployment would leave your King in check.",
    );
  }

  const players = updatePlayer(state.players, player, {
    hand: removeCard(hand, cardId),
  });
  const log: LogEntry = { kind: "deploy", player, piece: card.piece, to, ply: state.ply };
  const events: GameEvent[] = [{ type: "deployed", player, piece: card.piece, to }];

  return finish(state, { board, players, log }, events, true);
}

function applyPlayCard(
  state: GameState,
  player: PlayerId,
  cardId: string,
  from: Square | null,
  to: Square | null,
  reviveType: PieceTypeId | null,
): EngineResult {
  const hand = state.players[player].hand;
  const card = hand.find((c) => c.id === cardId);
  if (!card) return fail("Card not in hand.");
  if (card.kind !== "ability") return fail("That card is not an ability.");
  const def = abilityDef(card.ability);

  if (def.target === "relocate") {
    if (from === null || to === null) return fail("This card needs a target.");
    if (!abilitySources(state, player, def.id).includes(from)) {
      return fail("That piece can't be targeted by this card.");
    }
    if (!abilityDestinations(state, player, def.id, from).includes(to)) {
      return fail("That isn't a legal destination.");
    }

    const piece = state.board[from]!;
    let board = relocateBoard(state, from, to);
    // A relocation (Dash/Sidestep) that lands a promotable piece on its OWNER's
    // far rank promotes it, like a normal move reaching that rank. Keyed off
    // piece.owner (these cards can move either side's pieces); terrain (linked)
    // never promotes.
    let promotedTo: PieceTypeId | null = null;
    if (piece.link == null) {
      const pdef = pieceDef(piece.type);
      if (pdef.promotesTo && rankOf(to, state) === promotionRank(piece.owner, state.config)) {
        promotedTo = pdef.promotesTo;
        board = replaceAt(board, to, { ...board[to]!, type: promotedTo });
      }
    }
    // You may never play a card that leaves your own King in check — and while in
    // check, the card's effect must resolve it (same rule as moves and deploys).
    if (isInCheck({ ...state, board }, player)) return fail(inCheckRejection(state, player));
    const players = updatePlayer(state.players, player, { hand: removeCard(hand, cardId) });
    const events: GameEvent[] = [{ type: "moved", from, to }];
    if (promotedTo) events.push({ type: "promoted", at: to, to: promotedTo });
    events.push({ type: "cardPlayed", player, ability: def.id });
    const log: LogEntry = {
      kind: "card",
      player,
      ability: def.id,
      from,
      to,
      revived: null,
      ply: state.ply,
    };
    // Playing a card spends a finite hand resource, so it counts as progress.
    return finish(state, { board, players, log }, events, true);
  }

  if (def.target === "recall") {
    if (from === null) return fail("This card needs a target.");
    if (!abilitySources(state, player, def.id).includes(from)) {
      return fail("That piece can't be recalled.");
    }

    const piece = state.board[from]!;
    const owner = piece.owner;
    // A two-cell piece (Valley/Ridge) leaves the board entirely — both tiles.
    const board = recallBoard(state, from);
    // Recall can't leave (or fail to clear) your own King in check.
    if (isInCheck({ ...state, board }, player)) return fail(inCheckRejection(state, player));
    // The piece returns to ITS OWNER's hand as a fresh, redeployable card.
    const returned: Card = { id: `rcl${owner}-${state.ply}`, kind: "piece", piece: piece.type };
    // Remove the spell from the caster's hand first, then add the returned
    // piece to the owner's (possibly the same) hand so a self-recall nets zero.
    let players = updatePlayer(state.players, player, { hand: removeCard(hand, cardId) });
    players = updatePlayer(players, owner, { hand: [...players[owner].hand, returned] });

    const events: GameEvent[] = [
      { type: "recalled", piece: piece.type, from },
      { type: "cardPlayed", player, ability: def.id },
    ];
    const log: LogEntry = {
      kind: "card",
      player,
      ability: def.id,
      from,
      to: null,
      revived: null,
      ply: state.ply,
    };
    return finish(state, { board, players, log }, events, true);
  }

  if (def.target === "destroy") {
    if (from === null) return fail("This card needs a target.");
    if (!abilitySources(state, player, def.id).includes(from)) {
      return fail("That isn't terrain you can destroy.");
    }

    const piece = state.board[from]!;
    // The terrain is removed for good — both tiles of a two-cell Valley/Ridge —
    // and goes nowhere (not a hand, not the graveyard). It's not a capturable
    // combatant, so it isn't recorded as a capture.
    let board = replaceAt(state.board, from, null);
    if (piece.link != null) board = replaceAt(board, piece.link, null);
    // Clearing your own terrain can open a line onto your King; and it can never
    // resolve an existing check (terrain doesn't attack) — reject either way.
    if (isInCheck({ ...state, board }, player)) return fail(inCheckRejection(state, player));
    const players = updatePlayer(state.players, player, { hand: removeCard(hand, cardId) });

    const events: GameEvent[] = [
      { type: "captured", piece: piece.type, at: from },
      { type: "cardPlayed", player, ability: def.id },
    ];
    const log: LogEntry = {
      kind: "card",
      player,
      ability: def.id,
      from,
      to: null,
      revived: null,
      ply: state.ply,
    };
    return finish(state, { board, players, log }, events, true);
  }

  // Revive: pull a captured piece from the graveyard back into hand.
  if (reviveType === null || !abilityRevives(state, player).includes(reviveType)) {
    return fail("No such piece in your graveyard.");
  }
  // Reviving only adds a card to hand — it can't lift a check, so while your King
  // is in check you must address it first rather than durdle.
  if (isInCheck(state, player)) return fail("You're in check — resolve it first.");
  const foe = otherPlayer(player);
  const graveyard = state.players[foe].captured;
  const idx = graveyard.indexOf(reviveType);
  const nextGraveyard = [...graveyard.slice(0, idx), ...graveyard.slice(idx + 1)];

  // A distinct id prefix avoids colliding with the deck's `c{owner}-{n}` ids.
  const revived: Card = { id: `rev${player}-${state.ply}`, kind: "piece", piece: reviveType };
  let players = updatePlayer(state.players, foe, { captured: nextGraveyard });
  // Removing the spell and adding the revived piece nets to zero, so the hand
  // limit is never exceeded.
  players = updatePlayer(players, player, { hand: [...removeCard(hand, cardId), revived] });

  const events: GameEvent[] = [
    { type: "revived", player, piece: reviveType },
    { type: "cardPlayed", player, ability: def.id },
  ];
  const log: LogEntry = {
    kind: "card",
    player,
    ability: def.id,
    from: null,
    to: null,
    revived: reviveType,
    ply: state.ply,
  };
  return finish(state, { players, log }, events, true);
}

function applyDiscard(state: GameState, player: PlayerId, cardId: string): EngineResult {
  if (!mustDiscard(state, player)) return fail("You don't need to discard.");
  const hand = state.players[player].hand;
  const card = hand.find((c) => c.id === cardId);
  if (!card) return fail("Card not in hand.");

  const discarded = card.kind === "ability" ? card.ability : card.piece;
  const players = updatePlayer(state.players, player, { hand: removeCard(hand, cardId) });
  const log: LogEntry = { kind: "discard", player, card: discarded, ply: state.ply };

  // A forced discard does NOT end the turn — the player keeps discarding (then
  // takes their real action) on the same turn. Advancing ply lets reactive
  // drivers (UI, AI runner) re-evaluate; the idle counter resets since the hand
  // shrank irreversibly.
  return {
    ok: true,
    state: { ...state, players, ply: state.ply + 1, idlePlies: 0, log: [...state.log, log] },
    events: [{ type: "discarded", player, card: discarded }],
  };
}

/**
 * Manually draw a card as your whole turn. This is no longer offered in the UI
 * or used by the AI — every turn now begins with an automatic draw (see
 * autoDraw), so a player's action is always to move or play a card. The action
 * is retained as the underlying draw primitive (and for engine completeness).
 */
function applyDraw(state: GameState, player: PlayerId): EngineResult {
  const p = state.players[player];
  if (isInCheck(state, player)) {
    return fail("You're in check — you must move or block, not draw.");
  }
  if (p.deck.length === 0) return fail("Your deck is empty.");
  if (p.hand.length >= state.config.handLimit) return fail("Your hand is full.");

  const [top, ...rest] = p.deck;
  const players = updatePlayer(state.players, player, {
    deck: rest,
    hand: [...p.hand, top as Card],
  });
  const log: LogEntry = { kind: "draw", player, ply: state.ply };
  return finish(state, { players, log }, [{ type: "drew", player }], true);
}

function applyPass(state: GameState, player: PlayerId): EngineResult {
  if (isInCheck(state, player)) {
    return fail("You're in check — you must move or block, not pass.");
  }
  const log: LogEntry = { kind: "pass", player, ply: state.ply };
  return finish(state, { log }, [{ type: "passed", player }], false);
}

/**
 * The side to move flagged: their clock hit zero, so the opponent wins outright.
 * This bypasses the checkmate/stalemate/idle verdict — a loss on time is final
 * regardless of the position.
 */
function applyTimeout(state: GameState, player: PlayerId): EngineResult {
  const log: LogEntry = { kind: "timeout", player, ply: state.ply };
  const winner = otherPlayer(player);
  const base: GameState = { ...state, log: [...state.log, log] };
  return {
    ok: true,
    state: { ...base, ply: base.ply + 1, phase: "finished", winner },
    events: [{ type: "gameOver", winner }],
  };
}

/** A player concedes — the opponent wins outright, on whoever's turn it is. */
function applyResign(state: GameState, player: PlayerId): EngineResult {
  const log: LogEntry = { kind: "resign", player, ply: state.ply };
  const winner = otherPlayer(player);
  const base: GameState = { ...state, log: [...state.log, log] };
  return {
    ok: true,
    state: { ...base, ply: base.ply + 1, phase: "finished", winner },
    events: [{ type: "gameOver", winner }],
  };
}

/**
 * Open a draw offer. It doesn't consume the player's action or advance the turn
 * — it just records that `player` is offering. Only one offer may stand at a
 * time. The offer is cleared automatically the next time a turn-advancing action
 * is played (see `finish`), so it only lives until the recipient's next move.
 */
function applyOfferDraw(state: GameState, player: PlayerId): EngineResult {
  if (state.drawOffer != null) return fail("A draw offer is already pending.");
  return { ok: true, state: { ...state, drawOffer: player }, events: [] };
}

/** Accept the opponent's standing draw offer — the game ends in a draw. */
function applyAcceptDraw(state: GameState, player: PlayerId): EngineResult {
  if (state.drawOffer !== otherPlayer(player)) {
    return fail("There is no draw offer for you to accept.");
  }
  const log: LogEntry = { kind: "drawAgreed", player, ply: state.ply };
  const base: GameState = { ...state, log: [...state.log, log], drawOffer: null };
  return {
    ok: true,
    state: { ...base, ply: base.ply + 1, phase: "finished", winner: null },
    events: [{ type: "gameOver", winner: null }],
  };
}

/** Decline the opponent's standing draw offer — play continues. */
function applyDeclineDraw(state: GameState, player: PlayerId): EngineResult {
  if (state.drawOffer !== otherPlayer(player)) {
    return fail("There is no draw offer for you to decline.");
  }
  return { ok: true, state: { ...state, drawOffer: null }, events: [] };
}

// --- shared finalisation -----------------------------------------------------

interface StatePatch {
  board?: readonly (Piece | null)[];
  players?: readonly [PlayerState, PlayerState];
  log: LogEntry;
}

function finish(
  state: GameState,
  patch: StatePatch,
  events: GameEvent[],
  progress: boolean,
): EngineResult {
  const idlePlies = progress ? 0 : state.idlePlies + 1;
  const base: GameState = {
    ...state,
    board: patch.board ?? state.board,
    players: patch.players ?? state.players,
    log: [...state.log, patch.log],
    idlePlies,
    // A standing offer lapses when its *recipient* plays on (an implicit
    // decline). The offerer's own moves keep their offer alive for the opponent
    // to answer. `state.turn` is the side that just acted.
    drawOffer: state.drawOffer === state.turn ? state.drawOffer : null,
  };

  // Hand the turn to the opponent (who auto-draws), then judge their position.
  // The drawn card is part of the position they face — it can supply the deploy
  // that blocks a check — so the draw happens before the outcome is judged.
  const { state: next, events: drawEvents } = endTurn(base);
  const allEvents = [...events, ...drawEvents];
  const verdict = evaluateOutcome(next);

  // Checkmate: the side to move has no escape, so the mover wins. Judged before
  // the turn cap so a mate delivered on the final ply still counts as a win.
  if (verdict.kind === "checkmate") {
    const winner = otherPlayer(next.turn);
    const all = [...allEvents, { type: "gameOver", winner } as const];
    return { ok: true, state: { ...next, phase: "finished", winner }, events: all };
  }

  // The hard turn cap: once the game has run this long it's a draw, full stop.
  const maxPlies = state.config.maxPlies ?? DEFAULT_MAX_PLIES;
  if (next.ply >= maxPlies) {
    const capped: GameState = {
      ...next,
      log: [...next.log, { kind: "turnLimit", ply: next.ply }],
    };
    const all = [...allEvents, { type: "gameOver", winner: null } as const];
    return { ok: true, state: { ...capped, phase: "finished", winner: null }, events: all };
  }

  // Stalemate, or a no-progress draw: the game ends with no winner.
  if (verdict.kind === "stalemate" || idlePlies >= DRAW_AFTER_IDLE_PLIES) {
    const all = [...allEvents, { type: "gameOver", winner: null } as const];
    return { ok: true, state: { ...next, phase: "finished", winner: null }, events: all };
  }

  return { ok: true, state: next, events: allEvents };
}

// --- outcome detection -------------------------------------------------------

type Outcome =
  | { readonly kind: "checkmate" }
  | { readonly kind: "stalemate" }
  | { readonly kind: "ongoing" };

/**
 * Classify the position for the side to move in `state`:
 *  - checkmate: in check with no move and no blocking deploy to escape;
 *  - stalemate: not in check, but no move, no deployable square, and no draw —
 *    i.e. no constructive action available (a pass alone doesn't avert this);
 *  - ongoing: otherwise.
 */
function evaluateOutcome(state: GameState): Outcome {
  const player = state.turn;

  // A captured King is an immediate loss for its owner (only reachable once the
  // Prince is gone, since a guarded King can't be taken). Keyed off the actual
  // capture record — not merely a missing king — so minimal/test positions
  // without kings still play normally. Without it a King leaving the board would
  // fall through to the stalemate path and read as a draw.
  if (state.players[otherPlayer(player)].captured.includes("king")) {
    return { kind: "checkmate" };
  }

  const hasMove = allLegalMoves(state, player).length > 0;

  if (isInCheck(state, player)) {
    if (
      hasMove ||
      canDeployResolveCheck(state, player) ||
      canCardResolveCheck(state, player)
    ) {
      return { kind: "ongoing" };
    }
    return { kind: "checkmate" };
  }

  if (hasMove || hasConstructiveAction(state, player)) return { kind: "ongoing" };
  return { kind: "stalemate" };
}

/**
 * Squares the side to move could deploy at least one card in hand onto. If any
 * deployable card places anywhere (the Hill), every empty square qualifies;
 * otherwise only the home-rank squares do.
 */
function deployableTargets(state: GameState, player: PlayerId): Square[] {
  let anywhere = false;
  let home = false;
  for (const c of state.players[player].hand) {
    if (c.kind !== "piece" || !pieceDef(c.piece).deployable) continue;
    if (pieceDef(c.piece).placeAnywhere) anywhere = true;
    else home = true;
  }
  if (anywhere) {
    const out: Square[] = [];
    for (let sq = 0; sq < state.board.length; sq++) if (!state.board[sq]) out.push(sq);
    return out;
  }
  return home ? legalDeploySquares(state, player) : [];
}

/** Can the side to move deploy a piece that resolves an existing check? */
function canDeployResolveCheck(state: GameState, player: PlayerId): boolean {
  // The blocker's type is irrelevant to whether it interposes, so probe with a
  // placeholder piece on each candidate square.
  const probe: Piece = { id: "_probe", type: "pawn", owner: player, hasMoved: true };
  for (const sq of deployableTargets(state, player)) {
    const board = replaceAt(state.board, sq, probe);
    if (!isInCheck({ ...state, board }, player)) return true;
  }
  return false;
}

/**
 * Can the side to move lift an existing check by playing an ability card? Mirrors
 * the legality the reducer now enforces, so a position is only checkmate when no
 * move, blocking deploy, OR card can save the King. Relocate cards can shove the
 * attacker off its line or interpose a blocker; Recall can return the attacker to
 * hand. Demolish (terrain never attacks) and Resurrection (no board change) can't
 * resolve a check, so they're skipped. Only consulted when already in check.
 */
function canCardResolveCheck(state: GameState, player: PlayerId): boolean {
  for (const card of state.players[player].hand) {
    if (card.kind !== "ability") continue;
    const def = abilityDef(card.ability);
    if (def.target === "relocate") {
      for (const from of abilitySources(state, player, def.id)) {
        for (const to of abilityDestinations(state, player, def.id, from)) {
          if (!isInCheck({ ...state, board: relocateBoard(state, from, to) }, player)) return true;
        }
      }
    } else if (def.target === "recall") {
      for (const from of abilitySources(state, player, def.id)) {
        if (!isInCheck({ ...state, board: recallBoard(state, from) }, player)) return true;
      }
    }
  }
  return false;
}

/** A non-check position still has play if the side can deploy or draw a card. */
function hasConstructiveAction(state: GameState, player: PlayerId): boolean {
  const p = state.players[player];
  const canDeploy = deployableTargets(state, player).length > 0;
  const canDraw = p.deck.length > 0 && p.hand.length < state.config.handLimit;
  return canDeploy || canDraw;
}

function removeCard(hand: readonly Card[], cardId: string): Card[] {
  return hand.filter((c) => c.id !== cardId);
}

function rankOf(sq: Square, state: GameState): number {
  return Math.floor(sq / state.config.width);
}
